import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDirectSurveyCompletions } from '../BackendInterface';

interface UseDirectSurveyCompletionsOptions {
  classroomId?: string;
  formId: string;
  enabled?: boolean;
}

interface UseDirectSurveyCompletionsResult {
  completedStudentUserIds: string[];
  completedStudentUserIdSet: Set<string>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useDirectSurveyCompletions({
  classroomId,
  formId,
  enabled = true,
}: UseDirectSurveyCompletionsOptions): UseDirectSurveyCompletionsResult {
  const [completedStudentUserIds, setCompletedStudentUserIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  // Tracks the current in-flight request so out-of-order responses are discarded.
  const requestIdRef = useRef<number>(0);

  const loadCompletions = useCallback(async () => {
    if (!enabled || !classroomId) {
      setCompletedStudentUserIds([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Increment the request counter; capture this call's id for stale-response detection.
    const thisRequestId = ++requestIdRef.current;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getDirectSurveyCompletions(classroomId, formId);

      // Discard the result if a newer request has been initiated.
      if (thisRequestId !== requestIdRef.current) {
        return;
      }

      setCompletedStudentUserIds(response.completedStudentUserIds || []);
    } catch (loadError) {
      if (thisRequestId !== requestIdRef.current) {
        return;
      }
      console.error('Failed to load direct survey completions:', loadError);
      setCompletedStudentUserIds([]);
      setError('Unable to load completion status.');
    } finally {
      if (thisRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [classroomId, enabled, formId]);

  useEffect(() => {
    void loadCompletions();
  }, [loadCompletions]);

  const completedStudentUserIdSet = useMemo(() => {
    return new Set(completedStudentUserIds);
  }, [completedStudentUserIds]);

  return {
    completedStudentUserIds,
    completedStudentUserIdSet,
    isLoading,
    error,
    refresh: loadCompletions,
  };
}
